import React from "react";
import { RevButton, RevChip, RevTypeaheadMultiple } from "@revmatics/catalyst";
import type { RevInterestsInputProps } from "./interestsInputTypes";
import { instance } from "../../../../../../../../stores/axios/axiosConfig.ts";

const fetchInterests = async (query: string): Promise<string[]> => {
  if (!query) return [];
  try {
    const response = await instance.get(
      `/api/personas/taxonomy-nodes/typeahead/?q=${query}`,
    );
    return response.data;
  } catch (error) {
    console.error("Failed to fetch interests:", error);
    return [];
  }
};

export const RevInterestsInput: React.FC<RevInterestsInputProps> = ({
  name,
  label,
  isLabelBold,
  value: selectedInterests = [],
  onChange,
  error,
  required,
  className = "",
}) => {
  const handleAdd = (interest: string[] | null) => {
    if (!interest) return;

    onChange?.(interest);
  };

  const handleRemove = (interest: string) => {
    onChange?.(selectedInterests.filter((i) => i !== interest));
  };

  const handleClearAll = () => {
    onChange?.([]);
  };

  const labelTypographyClass = isLabelBold
    ? "typography-body-bold-sm"
    : "typography-body-xs";

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label
          className={`block ${labelTypographyClass} text-monochrome-900 mb-2 text-left`}
        >
          {label}
          {required && (
            <span className="text-red-500 text-sm font-medium ml-0.5">*</span>
          )}
        </label>
      )}
      {selectedInterests.length > 0 && (
        <div className="mb-2 p-3 bg-monochrome-50 rounded-md">
          <div className="flex items-center justify-end">
            <RevButton
              text="Clear all"
              displayAs="link"
              handleClick={handleClearAll}
              aria-label="Clear all selected interests"
              linkFontSize="small"
            />
          </div>

          <div className="flex flex-wrap gap-2 mt-2">
            {selectedInterests.map((interest) => (
              <RevChip
                key={interest}
                text={interest}
                clearBtn
                variant="medium"
                handleClick={() => handleRemove(interest)}
              />
            ))}
          </div>
        </div>
      )}
      <RevTypeaheadMultiple<string>
        name={name}
        loadOptions={fetchInterests}
        value={selectedInterests}
        onSelect={handleAdd}
        displayValue={(v) => v}
        placeholder="Search interests..."
        error={error?.toString()}
        renderOption={(interest) => (
          <span className="block truncate">{interest}</span>
        )}
        clearable
        debounceMs={300}
      />
    </div>
  );
};
